import { Injectable, inject } from '@angular/core'
import { matches, sortBy } from 'lodash-es'
import { PaymentOriginator, PaymentOriginatorApiService } from 'src/api/metadata/payment-originator'
import { ToasterService } from 'src/shared/toaster/toaster.service'

const CACHE_MAX_AGE = 3600 * 1000 // 1hr

export const NO_TRANSFER_ACCOUNTS_WARNING =
  'No transfer accounts are configured. Please contact your administrator.'

export const INCOMPLETE_TRANSFER_ACCOUNTS_WARNING =
  'Some transfer accounts are missing required bank details and were skipped. Please contact your administrator.'

type PaymentOriginatorQuery = Partial<Pick<PaymentOriginator, 'bank' | 'purpose' | 'currency'>>

const REQUIRED_FIELDS: Record<PaymentOriginator['purpose'], (keyof PaymentOriginator)[]> = {
  cross_border: ['client_id', 'account_number'],
  eft: ['originator_id', 'return_fi', 'return_account'],
}

const isComplete = (originator: PaymentOriginator) =>
  Boolean(REQUIRED_FIELDS[originator.purpose]?.every((field) => originator[field]))

@Injectable()
export class PaymentOriginatorsService {
  private readonly PaymentOriginatorApi = inject(PaymentOriginatorApiService)
  private readonly toaster = inject(ToasterService)


  private cacheExpiration = 0
  private cache: Promise<PaymentOriginator[]>

  /**
   * Get TradeCafe's own payment originator profiles, ordered by ranking.
   * Profiles missing a field their purpose requires are left out and reported.
   * Returns undefined when they could not be loaded.
   *
   * @param {any} query
   * @returns
   */
  async getPaymentOriginators(query: PaymentOriginatorQuery) {
    if (!this.cache || this.cacheExpiration < Date.now()) {
      this.cacheExpiration = Date.now() + CACHE_MAX_AGE
      // NOTE: we fetch everything
      this.cache = this.PaymentOriginatorApi.list({ limit: Number.MAX_SAFE_INTEGER })
        .then((res) => res.data)
        .catch((err) => {
          this.invalidateCache()
          throw err
        })
    }
    try {
      const matched = (await this.cache).filter(matches(query))
      const complete = matched.filter(isComplete)
      if (complete.length < matched.length) {
        this.toaster.warning(INCOMPLETE_TRANSFER_ACCOUNTS_WARNING)
      }
      return sortBy(complete, 'ranking')
    } catch (err) {
      this.toaster.error('Unable to load transfer accounts', err)
      return undefined
    }
  }

  invalidateCache() {
    this.cacheExpiration = 0
    this.cache = undefined
  }
}
